import { getQueryParam } from '@mi-front/core';
import debugFn from 'debug';
import Hls from 'hls.js';
import Player from 'video.js/dist/types/player';
import { getVodPreset, livePreset } from './presets';
import { HlsJsPreset } from './types';

const debug = debugFn('setupHlsJs');

/**
 * Mapping of preset names to preset configurations
 */
const presets: Record<string, HlsJsPreset> = {
  live: livePreset,
  vod: getVodPreset(),
};

/**
 * Sets up HLS.js for video playback with optimized configuration based on preset.
 * Configures buffer management, latency optimization, and memory limits.
 *
 * @param player - The Video.js player instance
 * @param src - The HLS source URL
 * @param preset - The preset name to use ('live' or 'vod'). Defaults to 'vod'
 */
export function setupHlsJs(player: Player, src: string, preset: string = 'vod'): void {
  debug('hls.js is supported, setting up hls.js');

  const selectedPreset = presets[preset] ?? presets.vod;

  if (!presets[preset]) {
    console.warn(`Unknown preset "${preset}", using "vod" preset`);
  }

  const hls = new Hls({
    debug: !!getQueryParam('hls_debug'),
    ...selectedPreset.config,
  });

  debug('using preset %s', preset);

  const videoEl = player.tech(false).el() as HTMLMediaElement;
  let sourceLoaded = false;

  /**
   * Initial attach: load the manifest once.
   * Recovery re-attach (recoverMediaError): keep the parsed manifest; hls.js resumes via startLoad().
   */
  const onMediaAttached = () => {
    if (!sourceLoaded) {
      sourceLoaded = true;
      debug('initial media attach, loading source');
      hls.loadSource(src);
      return;
    }

    debug('recovery media re-attach, skipping loadSource (hls.js owns startLoad)');
  };

  hls.on(Hls.Events.MEDIA_ATTACHED, onMediaAttached);

  hls.attachMedia(videoEl);

  // setupHlsErrorRecovery(hls, player, {
  //   autoRecoverMediaErrors: true,
  //   stopOnStall: !!getQueryParam('hls_stop_on_stall'),
  // });

  // Run preset-specific setup function if provided
  let teardownPresetSetup: (() => void) | undefined;
  if (selectedPreset.setup) {
    const teardown = selectedPreset.setup(hls, player);
    if (typeof teardown === 'function') {
      teardownPresetSetup = teardown;
    }
  }

  // Expose for debugging (levels, stats, etc.)
  // @ts-expect-error hls property exists but not in the videojs ts types
  player.hls = hls;

  // Keep memory clean
  player.one('dispose', () => {
    teardownPresetSetup?.();
    hls.off(Hls.Events.MEDIA_ATTACHED, onMediaAttached);
    hls.destroy();
  });
}
